<wr-base-dialog header="Prüfpunkteintrag" [showSubmitButton]="false">

  <button
    dialog-header-action
    type="button"
    class="go-to-button"
    (click)="navigateToInspection()"
    [nbTooltip]="'Begehung öffnen'"
    aria-label="Begehung öffnen">
    <nb-icon icon="external-link-outline" pack="eva"></nb-icon>
  </button>

  @if (detail$ | async; as detail) {
    <div class="detail-grid">
      <div class="detail-row">
        <span class="detail-label">Prüfpunkt</span>
        <span class="detail-value">{{ item.checkpointName }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Begehung</span>
        <span class="detail-value">{{ item.inspectionName }}</span>
      </div>
      @if (item.inspectionSendingTimestamp) {
        <div class="detail-row">
          <span class="detail-label">Begehungsdatum</span>
          <span class="detail-value">{{ item.inspectionSendingTimestamp | date: 'dd.MM.yyyy' }}</span>
        </div>
      }
      @if (showRepairTask) {
        <div class="detail-row" data-cy="defect-entry-repair-task">
          <span class="detail-label">Reparaturauftrag</span>
          <div class="detail-value repair-task" [nbSpinner]="repairTaskBusy()" nbSpinnerSize="small">
            @if (item.repairTaskId) {
              @if (repairTask(); as task) {
                <button type="button" class="repair-task-open" data-cy="repair-task-summary"
                        [disabled]="repairTaskBusy()"
                        (click)="openRepairTask(task)">
                  {{ task.title }}
                  @if (task.contractor) {
                    · {{ task.contractor.name }}
                  }
                  @if (task.sentAt) {
                    · versendet am {{ task.sentAt | date: 'dd.MM.yyyy' }}
                  }
                </button>
                <wr-repair-task-status-text data-cy="repair-task-status" [status]="task.status"></wr-repair-task-status-text>
                @if (task.completionComment || task.declineComment) {
                  <span class="repair-task-close-comment" data-cy="repair-task-close-comment-text">
                    {{ task.completionComment ? 'Abschluss' : 'Storno' }}:
                    {{ task.completionComment ?? task.declineComment }}
                    @if (task.closedByName) {
                      ({{ task.closedByName }})
                    }
                  </span>
                }
                <div class="repair-task-actions">
                  <button nbButton outline size="small" status="info" type="button"
                          data-cy="repair-task-open"
                          [disabled]="repairTaskBusy()"
                          (click)="openRepairTask(task)">
                    <nb-icon [icon]="task.permissions.canEdit ? 'edit-2-outline' : 'eye-outline'" pack="eva"></nb-icon>
                    {{ task.permissions.canEdit ? 'Bearbeiten' : 'Anzeigen' }}
                  </button>
                </div>
              } @else {
                <nb-spinner size="tiny"></nb-spinner>
              }
            } @else {
              <span>Kein Reparaturauftrag vorhanden</span>
              @if (canCreateRepairTask) {
                <button nbButton outline size="small" status="primary" type="button"
                        data-cy="repair-task-create"
                        [disabled]="repairTaskBusy()"
                        (click)="createRepairTask(detail)">
                  <nb-icon icon="plus-outline" pack="eva"></nb-icon>
                  Reparaturauftrag erstellen
                </button>
              }
            }
          </div>
        </div>
      }
      @if (detail.predefinedAssessment) {
        <div class="detail-row">
          <span class="detail-label">Feststellung</span>
          <span class="detail-value">{{ detail.predefinedAssessment.text }}</span>
        </div>
      }
      @if (detail.action) {
        <div class="detail-row">
          <span class="detail-label">Maßnahme</span>
          <span class="detail-value">{{ detail.action.name }}</span>
        </div>
      }
      @if (detail.deadline) {
        <div class="detail-row">
          <span class="detail-label">Frist</span>
          <span class="detail-value">{{ detail.deadline | deadlineTranslation }}</span>
        </div>
      }
      @if (detail.note) {
        <div class="detail-row">
          <span class="detail-label">Beschreibung</span>
          <span class="detail-value">{{ detail.note }}</span>
        </div>
      }
      @if (detail.buildingEquipments.length) {
        <div class="detail-row">
          <span class="detail-label">TGA</span>
          <div class="detail-value tga-list">
            @for (tga of detail.buildingEquipments; track tga.id) {
              <span class="tga-item">{{ tga.equipment.name }}</span>
            }
          </div>
        </div>
      }
      @if (detail.attachments.length) {
        <div class="detail-row">
          <span class="detail-label">Bilder & Dokumente</span>
          <div class="detail-value attachments">
            @for (att of detail.attachments; track att.s3key) {
              @if (att.type === 'IMAGE') {
                <img
                  [src]="att.presignedUrl"
                  class="attachment-image"
                  [title]="att.comment"
                  [alt]="att.comment || 'Bild'"
                  role="button"
                  tabindex="0"
                  (click)="openLightbox(att.presignedUrl)"
                  (keydown.enter)="openLightbox(att.presignedUrl)" />
              } @else {
                <span class="attachment-file">
                  <nb-icon pack="fas" icon="file"></nb-icon>
                  {{ att.fileName ?? att.type }}
                </span>
              }
            }
          </div>
        </div>
      }
      @if (detail.comments.length) {
        <div class="detail-row">
          <span class="detail-label">Kommentare</span>
          <div class="detail-value comments">
            @for (comment of detail.comments; track comment.id) {
              <div class="comment-item">
                <span class="comment-author">{{ comment.createdBy.firstName }} {{ comment.createdBy.lastName }}</span>
                <span class="comment-text">{{ comment.text }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  } @else {
    <div class="loading">
      <nb-spinner></nb-spinner>
    </div>
  }

  @if (lightboxUrl()) {
    <div class="lightbox-overlay" role="button" tabindex="0"
         (click)="closeLightbox()" (keydown.escape)="closeLightbox()">
      <img [src]="lightboxUrl()" class="lightbox-image" alt="Vergrößertes Bild" tabindex="-1"
           (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()" />
      <button class="lightbox-close" (click)="closeLightbox()">
        <nb-icon pack="eva" icon="close-outline"></nb-icon>
      </button>
    </div>
  }

  <button dialog-footer nbButton status="primary" (click)="exportPdf()"
          [disabled]="exporting()"
          [nbSpinner]="exporting()"
          nbSpinnerStatus="primary"
          nbSpinnerSize="small">
    <nb-icon pack="fas" icon="file-pdf"></nb-icon>
    PDF Export
  </button>

</wr-base-dialog>
